import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container, Row, Col } from 'reactstrap';

interface CoreFeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight: string;
  color: string;
  index: number;
}

const CoreFeature: React.FC<CoreFeatureProps> = ({
  icon,
  title,
  description,
  highlight,
  color,
  index,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const isEven = index % 2 === 0;

  return (
    <motion.div
      className={`core-feature ${isEven ? 'even' : 'odd'}`}
      initial={{ opacity: 0, x: isEven ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <div className="feature-content">
        {/* Icon */}
        <motion.div
          className="feature-icon-wrapper"
          style={{ '--feature-color': color } as React.CSSProperties}
          whileHover={prefersReducedMotion ? {} : { scale: 1.1 }}
        >
          <div className="icon-glow" />
          <div className="icon-inner">{icon}</div>
        </motion.div>

        {/* Content */}
        <div className="feature-text">
          <h4 className="feature-title">
            {title}
            <span className="feature-highlight" style={{ color }}>{highlight}</span>
          </h4>
          <p className="feature-description">{description}</p>
        </div>
      </div>

      {/* Decorative line */}
      <div className="feature-line" style={{ background: `linear-gradient(90deg, ${color}40, transparent)` }} />
    </motion.div>
  );
};

const CoreFeatures: React.FC = () => {
  const features = [
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      ),
      title: "Powerful VeRO Protection",
      description: "Detect risky brands and policy violations before listing. Our comprehensive VeRO database protects your account from suspensions and keeps you compliant.",
      highlight: " — Stay Safe",
      color: "#ef4444",
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      ),
      title: "Smart Brand Cleanup",
      description: "Automatically remove or replace brand names from your titles to avoid trademark issues. Keep your listings clean and compliant without manual editing.",
      highlight: " — Auto Clean",
      color: "#8b5cf6",
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
      title: "Profit Analytics",
      description: "Track your performance with detailed analytics. Calculate profits, monitor margins, and understand your business metrics at a glance with our intuitive dashboard.",
      highlight: " — Know Your Numbers",
      color: "#10b981",
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      ),
      title: "Intelligent Repricer",
      description: "Stay competitive with dynamic pricing that adjusts automatically. Monitor competitors, protect your margins, and maximise sales with our AI-powered repricer.",
      highlight: " — Beat Competition",
      color: "#f59e0b",
    },
  ];

  return (
    <section className="core-features-section" id="features">
      <Container>
        {/* Section Header */}
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge dark">
            <span className="badge-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </span>
            <span>Core Features</span>
          </div>
          <h2 className="section-title">
            Everything you need to <span className="gradient-text-alt">scale your business</span>
          </h2>
          <p className="section-subtitle">
            Built by dropshippers, for dropshippers. Every feature is designed to save time and maximise profits.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="core-features-grid">
          <Row>
            {features.map((feature, index) => (
              <Col lg={6} key={index} className="mb-4">
                <CoreFeature {...feature} index={index} />
              </Col>
            ))}
          </Row>
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="core-features-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="cta-content">
            <div className="cta-text">
              <h4>Ready to see all features?</h4>
              <p>Start your free trial and explore everything CodeRom has to offer.</p>
            </div>
            <a href={`${process.env.REACT_APP_APP_URL || ''}/register`} className="cta-button">
              <span>Start Free Trial</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>
        </motion.div>
      </Container>

      {/* Background elements */}
      <div className="core-features-bg">
        <div className="grid-pattern" />
      </div>
    </section>
  );
};

export default CoreFeatures;
